// ============================================================
// api/adminDeletedData.js
// Endpoint bindings for the admin-tenant deleted-data routes.
//
// Backend reference:
//   backend/func-carehub-api/src/functions/admin-client-deleted-data-list.js
//   backend/func-carehub-api/src/functions/admin-client-deleted-data-restore.js
//   backend/func-carehub-api/src/functions/admin-client-deleted-data-hard-delete.js
// ============================================================

import { apiGet, apiPost, apiDelete } from './client';

/**
 * List soft-deleted + client-permanent-deleted entities for a Client.
 *
 * @param {number} clientId
 * @param {Object} [params]
 * @param {string} [params.entityType]                 governance_event | compliance_policy | training_record | audit_instance | survey_contact
 * @param {boolean} [params.includeClientPermanentDeleted=true]
 *
 * @returns {Promise<{
 *   items: Array<{
 *     deletionLogId: number,
 *     entityType: string,
 *     entityId: string,
 *     action: 'soft_delete' | 'client_permanent_delete' | 'admin_hard_delete' | 'restore',
 *     actedBy: string,
 *     actedAt: string,
 *     reason: string | null,
 *     entitySnapshot: Object | null,
 *     currentState: 'soft_deleted' | 'client_permanent_deleted' | 'admin_hard_deleted' | 'restored' | 'unknown',
 *   }>
 * }>}
 */
export function listDeletedData(clientId, params = {}) {
  const qp = {};
  if (params.entityType) qp.entityType = params.entityType;
  if (params.includeClientPermanentDeleted === false) qp.includeClientPermanentDeleted = 'false';
  return apiGet(`/mgmt/admin/clients/${encodeURIComponent(clientId)}/deleted-data`, qp);
}

/**
 * Restore a soft-deleted or client-permanent-deleted entity by
 * deletionLogId. Records a fresh `restore` row in DeletionLog.
 *
 * @returns {Promise<{ restored: true, entityType: string, entityId: string }>}
 */
export function restoreDeletedEntity(clientId, deletionLogId) {
  return apiPost(`/mgmt/admin/clients/${encodeURIComponent(clientId)}/deleted-data/${encodeURIComponent(deletionLogId)}/restore`, {});
}

/**
 * Physically delete the entity referenced by deletionLogId, cascade
 * to dependent rows, best-effort blob cleanup.
 *
 * @returns {Promise<{ hardDeleted: true, entityType: string, entityId: string }>}
 */
export function hardDeleteEntity(clientId, deletionLogId) {
  return apiDelete(`/mgmt/admin/clients/${encodeURIComponent(clientId)}/deleted-data/${encodeURIComponent(deletionLogId)}`);
}
